import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { PERSON_API_SERVICE_TOKEN, SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { ScreeningActionPostPayload } from '@nk/data-entities-lib';
import { ButtonDirective, SpinnerComponent } from '@nk/ui-lib';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs/operators';
import { MonitoringConnectionService } from '../../services/monitoring-connection.service';
import { MonitoringOriginEntityService } from '../../services/monitoring-origin-entity.service';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import { DEFAULT_MONITORING_REMOVE_DECISION_COMMENT, ScreeningRemovePersonParams } from '../../utils/screening.utils';

@Component({
  selector: 'nk-screening-remove-person-modal',
  imports: [ReactiveFormsModule, TranslateModule, ButtonDirective, SpinnerComponent],
  templateUrl: './screening-remove-person-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningRemovePersonModalComponent implements ScreeningRemovePersonParams, OnInit {
  @Input() public screeningConfigUuid!: ScreeningRemovePersonParams['screeningConfigUuid'];
  @Input() public appMembershipUuid!: ScreeningRemovePersonParams['appMembershipUuid'];
  @Input()
  public entityMonitoringTypeUuid!: ScreeningRemovePersonParams['entityMonitoringTypeUuid'];
  @Input() public monitoringOriginEntityUuid!: ScreeningRemovePersonParams['monitoringOriginEntityUuid'];
  @Input() public screeningEntityUuid!: ScreeningRemovePersonParams['screeningEntityUuid'];
  public isLoading = false;
  public commentControl = new FormControl<string | null>(null, [Validators.required]);
  private _monitoringComment!: string;
  private _destroy = inject(DestroyRef);
  private _bsModalRef = inject(BsModalRef);
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _monitoringConnectionService = inject(MonitoringConnectionService);
  private _monitoringOriginEntityService = inject(MonitoringOriginEntityService);
  private _screeningCaseService = inject(ScreeningCaseService);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _cdr = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.commentControl.valueChanges.pipe(takeUntilDestroyed(this._destroy)).subscribe((data: string | null) => {
      this._monitoringComment = data ? data : '';
    });
  }

  public cancelAndClose(): void {
    this._bsModalRef.hide();
  }

  public removeAndClose(): void {
    const payload: ScreeningActionPostPayload = {
      entity_monitoring_type_uuid: this.entityMonitoringTypeUuid,
      app_membership_uuid: this.appMembershipUuid,
      monitoring_comment: this._monitoringComment,
      removed_flag: true,
      approved_flag: true,
      decision_comment: DEFAULT_MONITORING_REMOVE_DECISION_COMMENT,
      monitoring_origin_entity_uuid: this.monitoringOriginEntityUuid,
      screening_entity_uuid: this.screeningEntityUuid,
    };
    if (payload) {
      this._post(this.screeningConfigUuid, payload);
    }
  }

  private _post(screeningConfigUuid: string, payload: ScreeningActionPostPayload): void {
    this.isLoading = true;
    this._screeningConfigService
      .postScreeningActionEntity(screeningConfigUuid, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
      )
      .subscribe(() => {
        this._monitoringOriginEntityService.monitoringOriginEntityCompositeCollectionCache.invalidateAllCache();
        this._screeningCaseService.screeningEntityScreeningCaseCompositeCollectionCache.invalidateAllCache();
        this._monitoringConnectionService.monitoringConnectionCompositeCollectionCache.invalidateAllCache();
        this._personService.personRoleCompositeCollectionByPersonCache.invalidateAllCache();
        this._screeningConfigService.screeningActionCompositeCollectionCache.invalidateAllCache();
        this._screeningConfigService.screeningConfigStatsAggregateCache.invalidateAllCache();
        this._screeningInputParamsService.triggerScreeningEntitiesRefresh();
        this._bsModalRef.hide();
      });
  }
}
